import type { ReactNode } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { PaperLoader } from "src/components/loading";
import { Navigate, RouteObject } from "src/router";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import { prefetchContentBlocks } from "src/cep/messages/hooks/prefetch-content-blocks";
import { DrawerRouteGuard } from "src/cep/shared/drawers/drawer-route-guard";
import { getMetadataDrawerMode } from "src/cep/messages/editability";
import { canShowCampaignReporting } from "./campaign-detail/constants";
import { useCampaignOutletContext } from "./campaign-detail/outlet";
import { useCampaignHasSends } from "./campaign-detail/use-campaign-has-sends";

const CampaignsLoader = lazyWithRetry(() => import("./campaigns"));
const CampaignLoader = lazyWithRetry(() => import("./campaign-detail"));
const CampaignOverviewPage = lazyNamed(
  () => import("./campaign-detail/campaign-overview-page"),
  "CampaignOverviewPage",
);
const CampaignReportingPage = lazyNamed(
  () => import("./campaign-detail/campaign-reporting-page"),
  "CampaignReportingPage",
);
const CampaignRecipientsPage = lazyNamed(
  () => import("./campaign-detail/campaign-recipients-page"),
  "CampaignRecipientsPage",
);
// All campaign-detail drawers funnel through a single barrel so Vite/Rollup
// emits one shared chunk. The campaign-detail page also statically imports the
// barrel, making the drawer chunk a preload of the page chunk — by the time
// the user clicks any drawer trigger, the code is already in memory.
const drawers = () => import("./campaign-detail/drawers");
const MetadataDrawer = lazyNamed(drawers, "MetadataDrawer");
const SendLimitsDrawer = lazyNamed(drawers, "SendLimitsDrawer");
const AudienceDrawer = lazyNamed(drawers, "AudienceDrawer");
const RecipientsPreviewDrawer = lazyNamed(drawers, "RecipientsPreviewDrawer");
const ScheduleDrawer = lazyNamed(drawers, "ScheduleDrawer");
const RunsDrawer = lazyNamed(drawers, "RunsDrawer");

const MessageContentSurface = lazyNamed(
  () => import("src/cep/messages/message/message-content-surface"),
  "MessageContentSurface",
);

/**
 * A campaign that has never sent has no recipients to list.
 */
function CampaignRecipientsRoute({ children }: { children: ReactNode }) {
  const { campaign, derivedStatus } = useCampaignOutletContext();
  const { hasSends, isLoading } = useCampaignHasSends(campaign?.messages);

  // Hold the frame rather than redirecting: the count starts at zero, and a
  // redirect would unmount the component that would re-read it. Held with a
  // loader rather than nothing, so opening the tab is not a blank pane.
  if (isLoading) return <PaperLoader />;

  if (!canShowCampaignReporting(derivedStatus, hasSends)) {
    return <Navigate to="../overview" replace />;
  }

  return <>{children}</>;
}

function CampaignReportingRoute({ children }: { children: ReactNode }) {
  const { cepCampaignReporting } = useFlags();
  const { campaign, derivedStatus } = useCampaignOutletContext();
  const { hasSends, isLoading } = useCampaignHasSends(campaign?.messages);

  // Hold the frame rather than redirecting: the count starts at zero, and a
  // redirect would unmount the component that would re-read it.
  if (isLoading) return null;

  if (
    !cepCampaignReporting ||
    !canShowCampaignReporting(derivedStatus, hasSends)
  ) {
    return <Navigate to="../overview" replace />;
  }

  return <>{children}</>;
}

export const routes: RouteObject[] = [
  {
    path: "native-delivery/campaigns",
    spacesAccess: "hidden-from-limited-view",
    element: <CampaignsLoader />,
  },
  {
    path: "native-delivery/campaigns/:campaignId",
    spacesAccess: "hidden-from-limited-view",
    element: <CampaignLoader />,
    loader: prefetchContentBlocks,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="overview" replace />,
      },
      {
        path: "reporting",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <CampaignReportingRoute>
            <CampaignReportingPage />
          </CampaignReportingRoute>
        ),
      },
      {
        path: "recipients",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <CampaignRecipientsRoute>
            <CampaignRecipientsPage />
          </CampaignRecipientsRoute>
        ),
      },
      {
        path: "overview",
        spacesAccess: "hidden-from-limited-view",
        element: <CampaignOverviewPage />,
        children: [
          {
            path: "metadata",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard
                isAllowed={(s) =>
                  getMetadataDrawerMode(s, s.message.channel_type) != null
                }
              >
                <MetadataDrawer />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "content",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard>
                <MessageContentSurface />
              </DrawerRouteGuard>
            ),
          },
          {
            // A sibling of `content`, not a child, so the composer's `..`
            // still resolves to `overview` and Close leaves the drawer.
            path: "content/localize",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard>
                <MessageContentSurface initialTab="localize" />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "send-limits",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard isAllowed={(s) => s.canEditStructure}>
                <SendLimitsDrawer />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "audience",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard isAllowed={(s) => s.isDraft}>
                <AudienceDrawer />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "recipients-preview",
            spacesAccess: "hidden-from-limited-view",
            element: <RecipientsPreviewDrawer />,
          },
          {
            path: "schedule",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard isAllowed={(s) => s.isDraft}>
                <ScheduleDrawer />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "runs",
            spacesAccess: "hidden-from-limited-view",
            element: <RunsDrawer />,
          },
        ],
      },
    ],
  },
];
